/**
 * FieldTripDetail — displays the full lesson plan for a field trip with
 * grade-band tabs. Wired to a route: /academy/field-trip/:tripId
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar as CalendarIcon, BookOpen, Beaker, PenTool, Printer, Loader2 } from 'lucide-react';
import api from '../services/api';

const BAND_ORDER = ['elementary', 'middle', 'high'];

const FieldTripDetail = () => {
  const { tripId } = useParams();
  const [params] = useSearchParams();
  const initialGrade = params.get('grade');
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBand, setActiveBand] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/academy/field-trips/${tripId}`);
        setTrip(res.data);
      } finally { setLoading(false); }
    })();
  }, [tripId]);

  useEffect(() => {
    if (!trip) return;
    if (initialGrade) {
      const g = String(initialGrade);
      const guess = (['4','5'].includes(g) && 'elementary')
                 || (['6','7','8'].includes(g) && 'middle')
                 || (['9','10','11','12'].includes(g) && 'high')
                 || null;
      if (guess && trip.grade_bands.find((b) => b.id === guess)) { setActiveBand(guess); return; }
    }
    setActiveBand(trip.grade_bands[0]?.id || null);
  }, [trip, initialGrade]);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  if (!trip)   return <div className="min-h-screen bg-black text-white p-8">Field trip not found.</div>;

  const band = trip.grade_bands.find((b) => b.id === activeBand) || trip.grade_bands[0];

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      {/* Print CSS */}
      <style>{`@media print { .no-print { display:none !important } body { background:white !important; color:black !important } }`}</style>

      {/* Header */}
      <div className="border-b-2 border-zinc-900 no-print">
        <div className="max-w-5xl mx-auto p-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-[10px] font-black tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="ft-back">
            <ArrowLeft className="w-3 h-3" /> BACK
          </button>
          <button onClick={() => window.print()} className="text-[10px] font-black tracking-widest text-zinc-400 hover:text-amber-400 inline-flex items-center gap-1" data-testid="ft-print">
            <Printer className="w-3 h-3" /> PRINT LESSON PLAN
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <div className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color: '#F97316' }}>Field Trip · Curriculum Prep</div>
        <h1 className="text-3xl md:text-4xl font-black mt-2">{trip.title}</h1>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-zinc-400">
          <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> {trip.date}</span>
          {trip.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {trip.location}</span>}
          <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Grades: {trip.grades.join(', ')}</span>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed mt-4 max-w-3xl">{trip.hero_summary}</p>

        {/* Week theme */}
        <div className="mt-5 border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="text-[10px] font-black tracking-[0.28em] uppercase text-amber-400">Week Theme</div>
          <div className="text-lg font-black text-white mt-1">"{trip.week_theme}"</div>
          <div className="text-[11px] text-zinc-400 mt-1">Suggested week: {trip.week_window}</div>
        </div>
      </div>

      {/* Grade band tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-6 no-print">
        <div className="flex flex-wrap gap-2 border-b-2 border-zinc-900">
          {BAND_ORDER.map((bid) => {
            const b = trip.grade_bands.find((x) => x.id === bid);
            if (!b) return null;
            const active = activeBand === bid;
            return (
              <button
                key={bid}
                onClick={() => setActiveBand(bid)}
                className={`px-4 py-2 text-[10px] font-black tracking-[0.25em] uppercase border-b-2 transition-colors ${active ? 'text-amber-400 border-amber-500' : 'text-zinc-500 border-transparent hover:text-white'}`}
                data-testid={`ft-band-${bid}`}
              >
                {b.label.split('·')[0]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active band content */}
      {band && (
        <div className="max-w-5xl mx-auto px-4 mt-6" data-testid={`ft-band-content-${band.id}`}>
          <div className="text-[10px] font-black tracking-[0.28em] uppercase text-zinc-500">Grade Band</div>
          <h2 className="text-xl md:text-2xl font-black text-white mt-1">{band.label}</h2>

          {/* Standards */}
          <div className="mt-4 border border-zinc-800 p-3 bg-zinc-950/50">
            <div className="text-[10px] font-black tracking-[0.28em] uppercase text-amber-400 mb-2">California Standards Covered</div>
            <ul className="space-y-1">
              {band.standards.map((s, i) => (
                <li key={i} className="text-[11px] text-zinc-300 leading-snug flex gap-2">
                  <span className="text-amber-500 shrink-0">▸</span> {s}
                </li>
              ))}
            </ul>
          </div>

          {/* Daily lessons */}
          <div className="mt-6">
            <div className="text-[10px] font-black tracking-[0.28em] uppercase text-amber-400 mb-3">Daily Lesson Plan</div>
            <div className="space-y-3">
              {band.lessons.map((l, i) => (
                <div key={i} className="border-2 border-zinc-900 bg-zinc-950/40 p-4" data-testid={`ft-lesson-${band.id}-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-[9px] font-black tracking-[0.28em] uppercase text-zinc-500">{l.day}</div>
                      <h3 className="text-base md:text-lg font-black text-white mt-0.5">{l.title}</h3>
                    </div>
                    <div className="text-[9px] font-black tracking-widest text-amber-500/70">LESSON {i + 1}</div>
                  </div>
                  <p className="text-xs text-zinc-300 leading-relaxed mb-2"><span className="text-[9px] font-black tracking-widest text-amber-400 uppercase">Concept · </span>{l.concept}</p>
                  <p className="text-xs text-zinc-300 leading-relaxed mb-2"><span className="text-[9px] font-black tracking-widest text-orange-400 uppercase">iFLY Tie-In · </span>{l.ifly_tie}</p>
                  {l.materials && l.materials.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase mb-1">Materials</div>
                      <div className="flex flex-wrap gap-1">
                        {l.materials.map((m, mi) => (
                          <span key={mi} className="text-[10px] text-zinc-400 border border-zinc-800 px-1.5 py-0.5">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Cross-curricular tie-ins */}
          {(band.ela_tie_in || band.math_tie_in) && (
            <div className="mt-6 grid md:grid-cols-2 gap-3">
              {band.ela_tie_in && (
                <div className="border border-cyan-500/40 bg-cyan-500/5 p-3" data-testid={`ft-ela-${band.id}`}>
                  <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-400 flex items-center gap-1"><PenTool className="w-3 h-3" /> ELA Writing Tie-In</div>
                  <div className="text-[10px] text-zinc-500 mt-1">{band.ela_tie_in.standard}</div>
                  <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{band.ela_tie_in.prompt}</p>
                </div>
              )}
              {band.math_tie_in && (
                <div className="border border-emerald-500/40 bg-emerald-500/5 p-3" data-testid={`ft-math-${band.id}`}>
                  <div className="text-[10px] font-black tracking-[0.28em] uppercase text-emerald-400 flex items-center gap-1"><Beaker className="w-3 h-3" /> Math Tie-In</div>
                  <div className="text-[10px] text-zinc-500 mt-1">{band.math_tie_in.standard}</div>
                  <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{band.math_tie_in.prompt}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* All-school section */}
      <div className="max-w-5xl mx-auto px-4 mt-10">
        <div className="border-t-2 border-zinc-900 pt-6">
          <div className="text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: '#C9A961' }}>All-Grade Common Elements</div>
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div className="border border-zinc-800 p-3">
              <div className="text-[10px] font-black tracking-widest text-white uppercase mb-2">Vocab Wall</div>
              <div className="flex flex-wrap gap-1">
                {trip.common_vocab.map((v, i) => (<span key={i} className="text-[10px] text-zinc-300 border border-zinc-800 px-2 py-0.5">{v}</span>))}
              </div>
            </div>
            <div className="border border-zinc-800 p-3">
              <div className="text-[10px] font-black tracking-widest text-white uppercase mb-2">Field Trip Journal — Bus-Ride-Home Prompts</div>
              <ol className="text-[11px] text-zinc-300 leading-relaxed space-y-1 list-decimal list-inside">
                {trip.field_journal.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldTripDetail;
